import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import useIsNodeDisabled from '@components/MainStage/CustomNode/useIsNodeDisabled';
import useStore from '@components/MainStage/store';
import { useProject } from '@context/Project';
import useCanEditModel from '@hooks/useCanEditModel';

type NodesDataContextType = {
  getActiveNode: () => any;
  getActiveEdge: () => any;
  insightsTab: string;
  isConnectable: boolean;
  isNodeDisabled: (canvasNodeData: any) => boolean;
};

const defaultValue = {
  getActiveNode: () => null,
  getActiveEdge: () => null,
  insightsTab: '',
  isConnectable: false,
  isNodeDisabled: () => false,
};

const NodesDataContext = createContext<NodesDataContextType>(defaultValue);

export const NodesDataProvider: FC<PropsWithChildren> = ({ children }) => {
  const { getActiveNode, getActiveEdge } = useStore();
  const { insightsTab } = useProject();
  const isConnectable = useCanEditModel();
  const isNodeDisabled = useIsNodeDisabled();

  const value = useMemo(
    () => ({
      getActiveNode,
      getActiveEdge,
      insightsTab,
      isConnectable,
      isNodeDisabled,
    }),
    [insightsTab, isConnectable],
  );

  return (
    <NodesDataContext.Provider value={value}>
      {children}
    </NodesDataContext.Provider>
  );
};

export const useNodesDataContext = () => {
  const context = useContext(NodesDataContext);

  if (context === defaultValue) {
    throw new Error(
      'useNodesDataContext must be used within a NodesDataProvider',
    );
  }

  return context;
};
